/**
 * Users Contracts
 * Zod schemas for users API responses.
 * STRICT BUT CORRECT: RAW schemas validate actual backend payloads exactly.
 * Normalized schemas are separate for frontend consumption.
 *
 * Runtime boundary: Backend (snake_case) → RAW Schema validation → Normalizer → Frontend (camelCase)
 *
 * @module api/contracts/users
 */
import { z } from "zod"

// BoolLikeSchema is justified for boolean coercion - 0/1/true/false/"1"/"0" patterns
const BoolLikeSchema = z.union([z.boolean(), z.number(), z.string()])

// ============================================================================
// RAW BACKEND SCHEMAS (snake_case - STRICT validation of actual backend payloads)
// These schemas MUST match the exact backend response format
// ============================================================================

// RAW: /api/users/fields response
export const LegacyUserFieldSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  label: z.string(),
  required: BoolLikeSchema.optional(),
  show_in_list: BoolLikeSchema.optional(),
  searchable: BoolLikeSchema.optional(),
  default: z.any().nullable().optional(),
  options: z.any().nullable().optional(),
  unload: BoolLikeSchema.optional(),
})

export const LegacyUsersFieldsResponseSchema = z.array(LegacyUserFieldSchema)
export const UsersFieldsResponseSchema = LegacyUsersFieldsResponseSchema

// RAW: POST /edit/new_api/users/items response
// Backend returns: { users: [...], total: number }
// Users array contains objects with user_id, user_email fields and more
// Note: PHP mysqli returns user_id as string even though it's integer in DB
const LegacyUsersListItemSchema = z
  .object({
    user_id: z.number(),
    user_email: z.string(),
  })
  .passthrough() // Allow additional fields like login, name, phone, etc.

// STRICT RAW schema - NO transform/defaults that hide validation issues
export const LegacyUsersItemsResponseSchema = z.object({
  users: z.array(LegacyUsersListItemSchema),
  total: z.number(),
})

export const UsersItemsResponseSchema = LegacyUsersItemsResponseSchema

// RAW: GET /api/users/profile response
// Must have either id or user_id (integer in DB, string from PHP mysqli)
export const UsersProfileResponseSchema = z
  .object({
    id: z.number().optional(),
    user_id: z.number().optional(),
    email: z.string(),
    role: z.string(),
  })
  .passthrough()
  .refine((entry) => entry.id != null || entry.user_id != null, {
    message: "User profile must include id or user_id",
  })

/**
 * RAW schema for the current /api/users/profile error envelope.
 *
 * @precondition Backend rejected the stale route alias and returned the legacy
 * `{ status, errors[] }` structure.
 * @postcondition Successful parses preserve the backend error payload exactly so
 * smoke tests can assert the current 400 behavior without inventing a success
 * shape.
 * @throws ZodError when the backend response stops using the legacy error
 * envelope.
 */
export const UsersProfileErrorResponseSchema = z
  .object({
    status: z.union([z.number(), z.string()]),
    errors: z.array(z.string()),
    meta: z
      .object({
        request_id: z.string(),
      })
      .strict()
      .optional(),
  })
  .strict()

// RAW: GET /api/users/info/:id response
// Must have either id or user_id (integer in DB, string from PHP mysqli)
export const UsersInfoByIdResponseSchema = z
  .object({
    id: z.number().optional(),
    user_id: z.number().optional(),
    email: z.string(),
    role: z.union([z.number(), z.string()]),
    name: z.string().optional(),
    isActive: z.union([z.number(), z.string(), z.boolean()]).optional(),
    isBlocked: z.union([z.number(), z.string(), z.boolean()]).optional(),
  })
  .passthrough()
  .refine((entry) => entry.id != null || entry.user_id != null, {
    message: "User info must include id or user_id",
  })

// RAW: GET /edit/new_api/users/groups response
// Note: Groups have mixed format - strings, numbers, or objects with IDs
const LegacyUsersGroupItemSchema = z.union([
  z.string(),
  z.string(), // PHP mysqli returns numbers as strings
  z.object({
    id: z.number().optional(),
    name: z.string().optional(),
    label: z.string().optional(),
    title: z.string().optional(),
    value: z.number().optional(),
  }),
])

export const LegacyUsersGroupsResponseSchema = z.union([
  z.array(LegacyUsersGroupItemSchema),
  z.record(z.union([z.string(), z.string()])),
])

// RAW: GET /edit/new_api/users/:id response
// Based on actual backend: user_id (int in DB, string from PHP), user_email (str), login (str), user_blocked (int/bool)
export const LegacyUserDetailsResponseSchema = z.object({
  user_id: z.number(),
  user_email: z.string(),
  email: z.string().optional(), // Some endpoints use email instead of user_email
  login: z.string().optional(),
  password: z.string().optional(),
  name: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().optional(),
  user_blocked: z.union([z.number(), z.boolean()]).optional(),
  role: z.string().optional(),
  agree_new_rules: z.union([z.number(), z.boolean()]).optional(),
  favorites: z.array(z.any()).optional(),
  points: z.number().optional(),
  cart: z.record(z.any()).optional(),
  prev_id: z.number().nullable().optional(),
  prev_login: z.string().nullable().optional(),
  fb_id: z.string().nullable().optional(),
  vk_id: z.string().nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

// RAW: POST /edit/new_api/users response
export const LegacyCreateUserResponseSchema = z.object({
  result: z.string(),
  user_id: z.number().optional(),
  message: z.string().optional(),
})

// RAW: PUT /edit/new_api/users/:id response
export const LegacyUpdateUserResponseSchema = z.object({
  result: z.string(),
  message: z.string().optional(),
})

// RAW: DELETE /edit/new_api/users/:id response
export const LegacyDeleteUserResponseSchema = LegacyUpdateUserResponseSchema

// RAW: POST /edit/new_api/users/items_show_hide response
export const LegacyBulkBlockResponseSchema = z.object({
  result: z.string(),
  affected_count: z.number(),
  message: z.string().optional(),
})

// RAW: POST /edit/newreports/users response
export const LegacyExportUsersResponseSchema = z.object({
  result: z.string().optional(),
  download_url: z.string().optional(),
  message: z.string().optional(),
})

/**
 * RAW schema for the canonical backend login-as envelope.
 *
 * @precondition Payload comes from POST /api/users/login-as without frontend-side
 * transport rewrites or runtime schema validation.
 * @postcondition Successful parses preserve the backend-owned `{ status,
 * redirect_url }` envelope so normalization, not the transport layer, adapts it
 * for SPA consumers.
 * @mutates none
 * @throws {ZodError} When the backend stops returning the accepted login-as raw
 * contract.
 */
export const LegacyLoginAsUserResponseSchema = z.object({
  status: z.union([z.number(), z.string()]),
  redirect_url: z.string().optional(),
  token: z.string().optional(),
  message: z.string().optional(),
})

// Type exports for RAW schemas only
export const UserFieldsResponse = UsersFieldsResponseSchema
export const LegacyUsersFieldsResponse = LegacyUsersFieldsResponseSchema
export const LegacyUsersGroupsResponse = LegacyUsersGroupsResponseSchema
export const LegacyUsersItemsResponse = LegacyUsersItemsResponseSchema
